import { useQueryClient } from '@tanstack/react-query';
import { Translator } from 'Translator';
import LocalizedNoIndexLink from 'components/LocalizedNoIndexLink';
import StarRating from 'components/StarRating';
import parse from 'date-fns/parse';
import useUpvoteReview from 'hooks/useUpvoteReview';
import React from 'react';
import RoundedAvatar from 'reusable-components/avatars/RoundedAvatar';
import UpvoteButton from 'reusable-components/reviews/UpvoteButton';
import type { ExperienceReview as ExperienceReviewType } from 'src/hooks/useExperienceReviews';
import { useTrekker } from 'utils/analytics';
import getCountryFlagByCountryCode from 'utils/countryFlag';
import format from 'utils/formatDate';

const CSS = require('./ExperienceReview.module.scss');

interface ExperienceReviewOuterProps {
  experienceId?: string;
  reviewId: string;
  reviewer: {
    name: string;
    photoUrl: string;
    countryCode: string;
    countryName: string;
  };
  reviewee: any;
  date: string;
  rating: number;
  content: string;
  title: string;
  hostUrl?: string;
  isOriginals: boolean;
}

function ExperienceReview({
  experienceId,
  reviewId,
  reviewer,
  reviewee,
  date,
  rating,
  content,
  title,
  hostUrl,
  isOriginals,
}: ExperienceReviewOuterProps) {
  const createdAt = parse(date, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  const reviewerNameClean = reviewer.name?.split('@')[0];
  const trekker = useTrekker();
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<ExperienceReviewType>([
    'experience-review',
    reviewId,
  ]);
  const { addMutation, removeMutation, isLoading } = useUpvoteReview();
  const handleUpvoteClick = () => {
    if (data?.votedByCurrentUser) {
      trekker.event('click', {
        component: ['ExperienceReview'],
        review_id: reviewId,
        experience_id: experienceId,
        buttonText: 'remove_upvote',
      });
      removeMutation.mutate(reviewId);
    } else {
      trekker.event('click', {
        component: ['ExperienceReview'],
        review_id: reviewId,
        experience_id: experienceId,
        buttonText: 'add_upvote',
      });
      addMutation.mutate(reviewId);
    }
  };

  return (
    <div className={CSS.experienceReview}>
      <div className={CSS.experienceReviewHeader}>
        <RoundedAvatar
          key={reviewer.photoUrl}
          image={reviewer.photoUrl}
          size={56}
          color="yellow"
          alt={`Avatar ${reviewerNameClean}`}
          loading="lazy"
        />
        <div className={CSS.experienceReviewDetails}>
          <div className={CSS.experienceReviewName}>
            <span>{reviewerNameClean || 'Unknown'}</span>
            {reviewer.countryCode && (
              <span className={CSS.CountryFlag}>
                {getCountryFlagByCountryCode(reviewer.countryCode)}{' '}
                {reviewer.countryName}
              </span>
            )}
          </div>
          <div className={CSS.experienceReviewRating}>
            <StarRating rating={rating} />
            {isOriginals && (
              <span className={CSS.experienceReviewAbout}>
                {Translator.translate(
                  'ExperiencePage.Reviews.revieweeText',
                  {
                    reviewee: (
                      <LocalizedNoIndexLink
                        className={CSS.LocalLink}
                        to={`/host/${hostUrl}`}
                      >
                        {reviewee}
                      </LocalizedNoIndexLink>
                    ),
                  },
                  false,
                  '(About local {reviewee})',
                )}
              </span>
            )}
          </div>
          <div className={CSS.experienceReviewDate}>
            {format(createdAt, 'LL')}
          </div>
        </div>
        <UpvoteButton
          isLoading={isLoading}
          handleUpvoteClick={handleUpvoteClick}
          nrOfUpvotes={data?.votesUp}
          upvotedByCurrentUser={data?.votedByCurrentUser}
        />
      </div>
      <div className={CSS.experienceReviewContent}>
        {content}
        <span className={CSS.ReviewTitle}>{title}</span>
      </div>
    </div>
  );
}

export default ExperienceReview;
